import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { QrReader } from 'react-qr-reader';
import styles from './QrScan.module.less';

/**
 * Thin wrapper around react-qr-reader that:
 *  - Owns facingMode (env/user) and exposes a switchCamera() handle via ref.
 *  - Debounces repeat decodes so the same QR doesn't trigger 10 times per second.
 *  - Surfaces loading / camera-error states through props instead of internal UI.
 *  - Pauses cleanly when `active` is false (e.g. the modal is closing).
 *
 * Props:
 *   active       bool        — render the camera only when true (saves cpu/cam off-time)
 *   facingMode   'environment' | 'user'
 *   onDecode     (text: string) => void
 *   onCameraReady() => void    — called once the first video frame is available
 *   onCameraError(message: string) => void
 *   debounceMs   number        — minimum gap between successful decodes (default 800)
 */
const QrCamera = ({
  active,
  facingMode = 'environment',
  onDecode,
  onCameraReady,
  onCameraError,
  debounceMs = 800
}) => {
  const [ready, setReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const lastDecodeTimeRef = useRef(0);
  const lastDecodeTextRef = useRef(null);
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;
  const onReadyRef = useRef(onCameraReady);
  onReadyRef.current = onCameraReady;
  const onErrorRef = useRef(onCameraError);
  onErrorRef.current = onCameraError;

  // Reset ready/error when we re-mount the camera (active flip or facing change)
  useEffect(() => {
    if (!active) {
      setReady(false);
      setErrorMsg(null);
      lastDecodeTimeRef.current = 0;
      lastDecodeTextRef.current = null;
    }
  }, [active]);

  const handleResult = (result, error) => {
    // First successful frame → camera is alive
    if (!ready && (result || error)) {
      setReady(true);
      if (onReadyRef.current) onReadyRef.current();
    }
    if (!result) return;
    const text =
      typeof result.getText === 'function' ? result.getText() : result.text;
    if (!text) return;
    const now = Date.now();
    // Debounce: same code within window OR any code within window → ignore.
    // Doing both prevents the rapid-fire repeat fire from react-qr-reader (~10fps).
    if (now - lastDecodeTimeRef.current < debounceMs) return;
    lastDecodeTimeRef.current = now;
    lastDecodeTextRef.current = text;
    if (onDecodeRef.current) onDecodeRef.current(text);
  };

  // react-qr-reader v3 fires onError repeatedly for "no QR in frame" — that's
  // not a real failure. Real failures (permission denied, no camera) surface
  // via the constraints promise rejection inside the library, which it ALSO
  // routes through onError but only once. We detect "real" errors by name.
  // Anything we don't recognize as fatal, we ignore so the live feed stays.
  const handleScannerError = (err) => {
    if (!err) return;
    const name = err.name || '';
    const fatal =
      name === 'NotAllowedError' ||
      name === 'NotFoundError' ||
      name === 'NotReadableError' ||
      name === 'OverconstrainedError' ||
      name === 'SecurityError';
    if (!fatal) return;
    const friendly =
      name === 'NotAllowedError'
        ? 'Camera permission denied. Use manual entry below.'
        : name === 'NotFoundError'
        ? 'No camera found on this device. Use manual entry below.'
        : name === 'NotReadableError'
        ? 'Camera is in use by another application. Use manual entry below.'
        : 'Camera unavailable. Use manual entry below.';
    setErrorMsg(friendly);
    if (onErrorRef.current) onErrorRef.current(friendly);
  };

  if (!active) return null;

  return (
    <div className={styles.scanner_video}>
      <QrReader
        constraints={{ facingMode }}
        scanDelay={300}
        onResult={handleResult}
        onError={handleScannerError}
        videoStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
        videoContainerStyle={{ width: '100%', height: '100%', paddingTop: 0 }}
        containerStyle={{ width: '100%', height: '100%' }}
        ViewFinder={() => <div className={styles.scanner_reticle} />}
      />
      {!ready && !errorMsg && (
        <div className={styles.scanner_loading}>
          <Spin />
          <span>Starting camera…</span>
        </div>
      )}
      {errorMsg && (
        <div className={styles.scanner_error}>
          <span>{errorMsg}</span>
        </div>
      )}
    </div>
  );
};

export default QrCamera;
